import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { memberPaymentReducer, actions } from './MemberPaymentContext.reducer';
import { MemberPaymentContextProps, StripeMessage, initialMemberPaymentState } from './MemberPaymentContext.interface';
import mapStripeUpdateResponseToMessage from './mappers';

import { useMember } from 'modules/common/queries/member';
import { MemberStatus } from 'modules/common/interfaces/member';
import useUpdateStripePayment from 'services/member/mutations/useUpdateStripePayment';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

// Default no-op context. Consumers rendered outside MemberPaymentContextProvider
// (e.g. components shared across route trees we don't wrap, or unit tests that mount
// payment-adjacent components in isolation) get a safe, inert value instead of a
// runtime throw. The real provider below replaces this with live state.
const noopMemberPaymentContext: MemberPaymentContextProps = {
    ...initialMemberPaymentState,
    hasPaymentIssue: false,
    isPaymentPending: false,
    openPaymentModal: () => {},
    closePaymentModal: () => {},
    setStripeTokenId: () => {},
    setIsToSChecked: () => {},
    setStripeMessage: () => {},
    resetStripeMessage: () => {},
    updatePayment: async () => {},
};

const MemberPaymentContext = createContext<MemberPaymentContextProps>(noopMemberPaymentContext);

export function MemberPaymentContextProvider({ children }: { children?: React.ReactNode }) {
    const [state, dispatch] = useReducer(memberPaymentReducer, initialMemberPaymentState);
    const { data: member } = useMember();
    const queryClient = useQueryClient();
    const stripeUpdateMutation = useUpdateStripePayment();

    const hasPaymentIssue = member?.status === MemberStatus.PAYMENT_ERROR;
    const isPaymentPending = member?.status === MemberStatus.PENDING;

    const openPaymentModal = useCallback(() => {
        dispatch(actions.openModal());
    }, []);

    const closePaymentModal = useCallback(() => {
        // Reset everything (modal-open flag, token, ToS, message) so the next open
        // starts from a clean slate. The Dialog's children (CardForm + Stripe iframes)
        // unmount with the Dialog itself, so card/exp/cvc/zip clear naturally.
        dispatch(actions.reset());
    }, []);

    const setStripeTokenId = useCallback((id: string) => {
        dispatch(actions.setStripeTokenId(id));
    }, []);

    const setIsToSChecked = useCallback((checked: boolean) => {
        dispatch(actions.setIsToSChecked(checked));
    }, []);

    const setStripeMessage = useCallback((msg: Partial<StripeMessage>) => {
        dispatch(actions.setStripeMessage(msg));
    }, []);

    const resetStripeMessage = useCallback(() => {
        dispatch(actions.resetStripeMessage());
    }, []);

    const updatePayment = useCallback(async () => {
        // Defensive guard: the Update button is normally disabled until both the
        // tokenized card and the member's email are present, but if updatePayment
        // is invoked programmatically (or member data hasn't loaded yet) we'd
        // otherwise POST an empty request. Bail early with a deterministic message.
        if (!member?.email || !state.stripeTokenId) {
            dispatch(
                actions.setStripeMessage({
                    isSuccess: false,
                    firstTime: false,
                    message: 'There was a problem updating your payment method. Please refresh and try again.',
                })
            );
            return;
        }

        const firstStatus = member.status;
        dispatch(actions.setUpdating(true));

        try {
            const result = await stripeUpdateMutation.mutateAsync({
                email: member.email,
                token: state.stripeTokenId,
            });

            dispatch(actions.setStripeMessage(mapStripeUpdateResponseToMessage(result, firstStatus)));

            if (result.success) {
                dispatch(actions.setStripeTokenId(''));
                dispatch(actions.setIsToSChecked(false));
                queryClient.invalidateQueries({ queryKey: ['member'] });
            }
        } catch (error) {
            // collectiveApi's response interceptor already reports axios errors. Anything
            // that reaches this catch from non-API sources (e.g. a defect in
            // mapStripeUpdateResponseToMessage) would otherwise be swallowed.
            sendDatadogRumError('MemberPaymentContext.updatePayment', error as Error, {
                source: 'MemberPaymentContext',
            });
            dispatch(
                actions.setStripeMessage({
                    isSuccess: false,
                    firstTime: false,
                    message: 'There was a problem processing your payment. Please check your connection and try again.',
                })
            );
        } finally {
            dispatch(actions.setUpdating(false));
        }
    }, [member?.status, member?.email, state.stripeTokenId, stripeUpdateMutation, queryClient]);

    useEffect(() => {
        if (hasPaymentIssue || !state.stripeMessage.isSuccess) {
            return undefined;
        }
        const timeout = setTimeout(() => {
            dispatch(actions.reset());
        }, 6000);
        return () => clearTimeout(timeout);
    }, [hasPaymentIssue, state.stripeMessage.isSuccess]);

    const contextValue = useMemo<MemberPaymentContextProps>(
        () => ({
            ...state,
            hasPaymentIssue,
            isPaymentPending,
            openPaymentModal,
            closePaymentModal,
            setStripeTokenId,
            setIsToSChecked,
            setStripeMessage,
            resetStripeMessage,
            updatePayment,
        }),
        [
            state,
            hasPaymentIssue,
            isPaymentPending,
            openPaymentModal,
            closePaymentModal,
            setStripeTokenId,
            setIsToSChecked,
            setStripeMessage,
            resetStripeMessage,
            updatePayment,
        ]
    );

    return <MemberPaymentContext.Provider value={contextValue}>{children}</MemberPaymentContext.Provider>;
}

export function useMemberPaymentContext() {
    return useContext(MemberPaymentContext);
}
